import React, { useState, useEffect } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { useTheme } from '../contexts/ThemeContext';
import ThemedSelect from './shared/ThemedSelect';

const JournalPromptModal = ({ show, onHide, onSave, field = null, fieldIndex = null }) => {
  const { colors } = useTheme();
  const [label, setLabel] = useState('');
  const [type, setType] = useState('text');
  const [options, setOptions] = useState([]);
  const [optionInput, setOptionInput] = useState('');
  const [max, setMax] = useState(5);
  const [editingOption, setEditingOption] = useState({ index: null, value: '' });

  // Initialize form when modal opens or field changes
  useEffect(() => {
    if (show) {
      if (field) {
        // Editing existing field
        setLabel(field.label || '');
        setType(field.type || 'text');
        // Convert object options to strings for editing (we'll convert back on save)
        const opts = (field.options || []).map(opt => 
          typeof opt === 'string' ? opt : (opt.label || opt.value || String(opt))
        );
        setOptions(opts);
        setMax(field.max || 5);
      } else {
        // New field
        setLabel('');
        setType('text');
        setOptions([]);
        setMax(5);
      }
      setOptionInput('');
      setEditingOption({ index: null, value: '' });
    }
  }, [show, field]);

  const handleAddOption = () => {
    const value = optionInput.trim();
    if (!value) return;
    // Check if value already exists (handle both string and object formats)
    const exists = options.some(opt => {
      const optValue = typeof opt === 'string' ? opt : (opt.value || opt.label || String(opt));
      return optValue === value;
    });
    if (exists) return;
    setOptions([...options, value]);
    setOptionInput('');
  };

  const handleRemoveOption = (index) => {
    setOptions(options.filter((_, i) => i !== index));
  };

  const handleEditOption = (index, currentValue) => {
    setEditingOption({ index, value: currentValue });
  };

  const handleCommitEditOption = () => {
    if (editingOption.index == null) return;
    const value = editingOption.value.trim();
    if (!value) {
      handleRemoveOption(editingOption.index);
    } else {
      const newOptions = [...options];
      newOptions[editingOption.index] = value;
      // Remove duplicates
      const uniqueOptions = newOptions.filter((o, i, arr) => arr.indexOf(o) === i);
      setOptions(uniqueOptions);
    }
    setEditingOption({ index: null, value: '' });
  };

  const handleCancelEditOption = () => {
    setEditingOption({ index: null, value: '' });
  };

  const handleSave = () => {
    const fieldData = {
      label: label.trim() || undefined,
      type,
      ...((type === 'single' || type === 'multi') && { options }),
      ...(type === 'rank' && { max })
    };
    onSave(fieldData, fieldIndex);
    onHide();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAddOption();
    }
  };

  const isValid = label.trim().length > 0 && ((type !== 'single' && type !== 'multi') || options.length > 0);

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      centered 
      contentClassName="bg-dark" 
      backdropClassName="bg-opacity-50"
      style={{ zIndex: 10000 }}
    >
      <Modal.Header 
        closeButton 
        closeVariant="white" 
        style={{ 
          borderBottom: `1px solid ${colors.borderLight}`, 
          color: colors.text, 
          background: colors.bgSecondary 
        }}
      >
        <Modal.Title style={{ fontSize: 16, color: colors.text }}>
          {field ? 'Edit Journal Prompt' : 'Add Journal Prompt'}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body style={{ background: colors.bgSecondary, padding: '1.5rem' }}>
        <Form>
          {/* Prompt Label */}
          <Form.Group className="mb-3">
            <Form.Label style={{ color: colors.text, fontSize: '0.9rem', fontWeight: 500, marginBottom: '0.5rem' }}>
              Prompt Label
            </Form.Label>
            <Form.Control
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Enter prompt label"
              autoFocus
              style={{
                backgroundColor: colors.bgInput,
                borderColor: colors.border,
                color: colors.text,
                fontSize: '0.9rem',
                padding: '0.5rem'
              }}
            />
          </Form.Group>

          {/* Input Type */}
          <Form.Group className="mb-3">
            <Form.Label style={{ color: colors.text, fontSize: '0.9rem', fontWeight: 500, marginBottom: '0.5rem' }}>
              Input Type
            </Form.Label>
            <ThemedSelect
              value={type}
              options={[
                { value: 'text', label: 'Text' },
                { value: 'datetime', label: 'Date/Time' },
                { value: 'rank', label: 'Rank 1-5' },
                { value: 'single', label: 'Single Select' },
                { value: 'checkbox', label: 'Checkbox' },
                { value: 'number', label: 'Number' },
                { value: 'currency', label: 'Currency' }
              ]}
              onChange={setType}
              width="100%"
            />
          </Form.Group>

          {/* Options for Single Select */}
          {type === 'single' && (
            <Form.Group className="mb-3">
              <Form.Label style={{ color: colors.text, fontSize: '0.9rem', fontWeight: 500, marginBottom: '0.5rem' }}>
                Options
              </Form.Label>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <Form.Control
                  type="text"
                  value={optionInput}
                  onChange={(e) => setOptionInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type option and press Enter or ,"
                  style={{
                    backgroundColor: colors.bgInput,
                    borderColor: colors.border,
                    color: colors.text,
                    fontSize: '0.9rem',
                    padding: '0.5rem',
                    flex: 1
                  }}
                />
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleAddOption}
                  disabled={!optionInput.trim()}
                  style={{
                    borderColor: colors.primary,
                    color: colors.primary,
                    whiteSpace: 'nowrap'
                  }}
                >
                  Add
                </Button>
              </div>
              {options.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {options.map((opt, i) => {
                    const isEditing = editingOption.index === i;
                    return (
                      <span
                        key={i}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          backgroundColor: colors.bgInput,
                          color: colors.text,
                          border: `1px solid ${colors.border}`,
                          borderRadius: 10,
                          padding: '4px 8px',
                          fontSize: '0.85rem'
                        }}
                      >
                        {isEditing ? (
                          <input
                            value={editingOption.value}
                            onChange={(e) => setEditingOption(prev => ({ ...prev, value: e.target.value }))}
                            onBlur={handleCommitEditOption}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleCommitEditOption();
                              if (e.key === 'Escape') handleCancelEditOption();
                            }}
                            autoFocus
                            style={{
                              background: 'transparent',
                              border: 'none',
                              outline: 'none',
                              color: colors.text,
                              minWidth: 40,
                              fontSize: '0.85rem'
                            }}
                          />
                        ) : (
                          <span
                            onClick={() => handleEditOption(i, typeof opt === 'string' ? opt : (opt.label || opt.value || String(opt)))}
                            style={{ cursor: 'text' }}
                          >
                            {typeof opt === 'string' ? opt : (opt.label || opt.value || String(opt))}
                          </span>
                        )}
                        <button
                          title="Remove option"
                          aria-label="Remove option"
                          onClick={() => handleRemoveOption(i)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            outline: 'none',
                            color: colors.danger,
                            cursor: 'pointer',
                            padding: 0,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginLeft: 4
                          }}
                        >
                          <span style={{ fontSize: '0.75rem' }}>×</span>
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              {options.length === 0 && (
                <p style={{ color: colors.textMuted, fontSize: '0.8rem', marginTop: '0.5rem', fontStyle: 'italic' }}>
                  Add at least one option
                </p>
              )}
            </Form.Group>
          )}

          {/* Max value for Rank */}
          {type === 'rank' && (
            <Form.Group className="mb-3">
              <Form.Label style={{ color: colors.text, fontSize: '0.9rem', fontWeight: 500, marginBottom: '0.5rem' }}>
                Maximum Rank Value
              </Form.Label>
              <Form.Control
                type="number"
                min="2"
                max="10"
                value={max}
                onChange={(e) => setMax(parseInt(e.target.value || '5', 10))}
                style={{
                  backgroundColor: colors.bgInput,
                  borderColor: colors.border,
                  color: colors.text,
                  fontSize: '0.9rem',
                  padding: '0.5rem'
                }}
              />
            </Form.Group>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer style={{ background: colors.bgSecondary, borderTop: `1px solid ${colors.borderLight}` }}>
        <Button
          variant="outline-secondary"
          onClick={onHide}
          style={{
            borderColor: colors.border,
            color: colors.text
          }}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!isValid}
          style={{
            backgroundColor: colors.primary,
            borderColor: colors.primary,
            color: 'white'
          }}
        >
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default JournalPromptModal;











